import { httpClient } from './httpClient';

export interface TicketStats {
  totalTickets: number;
  ticketsCompleted: number;
  averageResponseTime: string;
  ticketsByStatus: {
    new: number;
    processing: number;
    resolved: number;
    closed: number;
  };
  ticketsByPriority: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
}

interface TrendData {
  date: string;
  created_count: number;
  closed_count: number;
}

export interface TopContributor {
  id: number;
  name: string;
  resolvedCount: number;
  inProgressCount: number;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export type DashboardStats = TicketStats;

class DashboardService {
  // Get ticket statistics
  async getTicketStats(dateRange: DateRange): Promise<DashboardStats> {

    const queryParams = new URLSearchParams();
    
    // Create dates in Myanmar timezone (UTC+6:30)
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // Set time to start and end of day
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    // Convert to ISO string
    queryParams.append('startDate', startDate.toISOString());
    queryParams.append('endDate', endDate.toISOString());
    
    const url = `/api/dashboard/stats?${queryParams.toString()}`;
    
    try {
      return await httpClient.get<DashboardStats>(url);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Return default stats if API fails
      return {
        totalTickets: 0,
        ticketsCompleted: 0,
        averageResponseTime: '0h 0m',
        ticketsByStatus: { new: 0, processing: 0, resolved: 0, closed: 0 },
        ticketsByPriority: { low: 0, medium: 0, high: 0, urgent: 0 }
      };
    }
  }

  // Get ticket trends for the chart
  async getTicketTrends(dateRange: DateRange): Promise<TrendData[]> {
    // Create dates in Myanmar timezone (UTC+6:30)
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // Set time to start and end of day
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    // Convert to ISO string
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', startDate.toISOString());
    queryParams.append('endDate', endDate.toISOString());
    
    const url = `/api/dashboard/trends?${queryParams.toString()}`;
    return httpClient.get<TrendData[]>(url);
  }

  // Get top contributors
  async getTopContributors(dateRange: DateRange): Promise<TopContributor[]> {
    
    // Create dates in Myanmar timezone (UTC+6:30)
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // Set time to start and end of day
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    // Convert to ISO string
    const queryParams = new URLSearchParams();
    queryParams.append('startDate', startDate.toISOString());
    queryParams.append('endDate', endDate.toISOString());
    
    const url = `/api/dashboard/contributors?${queryParams.toString()}`;
    return httpClient.get<TopContributor[]>(url);
  }
}

export const dashboardService = new DashboardService();
